import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    DirectionsBike,
    Edit as EditIcon,
    Explore,
    Forest,
    Hiking,
    Hotel,
    Landscape,
    LocalCafe,
    LocalDining,
    Pool,
    Restaurant,
    RoomService,
    Spa,
    Terrain,
    TravelExplore,
    UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import usePopularTopics from "../../hooks/usePopularTopics";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const MATERIAL_ICON_OPTIONS = [
    { value: "Hiking", label: "Wandern", Icon: Hiking },
    { value: "Spa", label: "Wellness", Icon: Spa },
    { value: "DirectionsBike", label: "E-Bike", Icon: DirectionsBike },
    { value: "Landscape", label: "Berge", Icon: Landscape },
    { value: "Terrain", label: "Skigebiet", Icon: Terrain },
    { value: "LocalDining", label: "Gourmet", Icon: LocalDining },
    { value: "Restaurant", label: "Restaurant", Icon: Restaurant },
    { value: "Hotel", label: "Hotel", Icon: Hotel },
    { value: "TravelExplore", label: "Ausflugsziel", Icon: TravelExplore },
    { value: "Explore", label: "Entdecken", Icon: Explore },
    { value: "Pool", label: "Pool", Icon: Pool },
    { value: "RoomService", label: "Service", Icon: RoomService },
    { value: "LocalCafe", label: "Cafe", Icon: LocalCafe },
    { value: "Forest", label: "Natur", Icon: Forest },
];

const blank = {
    title: "",
    search_query: "",
    icon_name: "TravelExplore",
    sort_order: 0,
    is_active: true,
};

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) return value;
    if (value.startsWith("/images/")) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

function getIcon(name) {
    return MATERIAL_ICON_OPTIONS.find((item) => item.value === name)?.Icon || TravelExplore;
}

export default function PopularTopicsPage() {
    const { topics, loading, error, fetchTopics, createTopic, updateTopic, deleteTopic } = usePopularTopics();
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(blank);
    const [editing, setEditing] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState("");
    const [submitError, setSubmitError] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        return () => {
            if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const rows = React.useMemo(() => {
        return [...(topics || [])].sort((a, b) => (
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.title || "").localeCompare(String(b.title || ""), "de")
        ));
    }, [topics]);

    const setPickedFile = (file) => {
        if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        setImageFile(file || null);
        setImagePreview(file ? URL.createObjectURL(file) : "");
    };

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setPickedFile(null);
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (topic) => {
        setEditing(topic);
        setForm({
            ...blank,
            ...topic,
            sort_order: topic.sort_order ?? 0,
            is_active: topic.is_active !== undefined ? !!topic.is_active : true,
        });
        setPickedFile(null);
        setImagePreview(resolveUrl(topic.image_url));
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        const title = (form.title || "").trim();
        if (!title) {
            setSubmitError("Titel ist Pflichtfeld.");
            return;
        }

        const payload = {
            title,
            search_query: (form.search_query || "").trim() || title,
            icon_name: form.icon_name || "TravelExplore",
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updateTopic(editing.id, payload, imageFile);
            else await createTopic(payload, imageFile);
            setOpen(false);
        } catch (e) {
            setSubmitError(e?.response?.data?.error || e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="Beliebte Themen"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchTopics} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neues Thema
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Bild</TableCell>
                                <TableCell>Icon</TableCell>
                                <TableCell>Titel</TableCell>
                                <TableCell>Suchbegriff</TableCell>
                                <TableCell>Aktiv</TableCell>
                                <TableCell>Sortierung</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((topic) => {
                                const Icon = getIcon(topic.icon_name);
                                return (
                                    <TableRow key={topic.id} hover>
                                        <TableCell>
                                            {topic.image_url ? (
                                                <Box component="img" src={resolveUrl(topic.image_url)} alt={topic.title} sx={{ width: 96, height: 54, borderRadius: 1, objectFit: "cover" }} />
                                            ) : (
                                                <Alert severity="info" icon={false} sx={{ py: 0 }}>Kein Bild</Alert>
                                            )}
                                        </TableCell>
                                        <TableCell><Icon sx={{ color: "#ff8a18" }} /></TableCell>
                                        <TableCell>{topic.title}</TableCell>
                                        <TableCell>{topic.search_query || topic.title}</TableCell>
                                        <TableCell>{topic.is_active ? "Ja" : "Nein"}</TableCell>
                                        <TableCell>{topic.sort_order ?? 0}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => openEdit(topic)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => { setDeleteTarget(topic); setDeleteError(""); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Thema bearbeiten" : "Neues Thema anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <TextField label="Titel*" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                        <TextField label="Suchbegriff" helperText="Leer lassen, um den Titel zu verwenden" value={form.search_query || ""} onChange={(e) => setForm((f) => ({ ...f, search_query: e.target.value }))} />
                        <FormControl fullWidth>
                            <InputLabel>Material Icon</InputLabel>
                            <Select
                                label="Material Icon"
                                value={form.icon_name || "TravelExplore"}
                                onChange={(e) => setForm((f) => ({ ...f, icon_name: e.target.value }))}
                            >
                                {MATERIAL_ICON_OPTIONS.map(({ value, label, Icon }) => (
                                    <MenuItem key={value} value={value}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Icon fontSize="small" sx={{ color: "#ff8a18" }} />
                                            {label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Sortierung" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />

                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            Bild hochladen / ersetzen
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    setPickedFile(e.target.files?.[0] || null);
                                    e.target.value = "";
                                }}
                            />
                        </Button>

                        {imagePreview && (
                            <Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 160 }}>
                                <Box component="img" src={imagePreview} alt="Vorschau" sx={{ maxWidth: "100%", maxHeight: 150, borderRadius: 1, objectFit: "cover" }} />
                            </Paper>
                        )}
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern..." : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Thema löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du <b>{deleteTarget?.title}</b> wirklich löschen?
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteTopic(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e?.response?.data?.error || e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
